/**
 * Mux playback IDs — defaults del brand (creator_id en Mux: juaperrone).
 *
 * Operaciones recomendadas (Mux CLI — https://www.mux.com/docs/integrations/mux-cli):
 *
 *   mux login --env-file .env
 *   mux playback-ids <playback-id> --expand     # lookup asset + policy
 *   mux assets update <asset-id> --creator-id juaperrone --external-id feed
 *   mux webhooks listen --forward-to http://localhost:8080/api/webhooks/mux
 *
 * Playback policy `public` → URL directa en @mux/mux-player-react.
 * Policy `signed` → requiere `mux sign <playback-id>` o JWT server-side.
 */
import { BRAND_MUX_CREATOR_ID } from "@/lib/brandConfig";
import { isValidMuxPlaybackId } from "../../shared/muxPlaybackId";

export { isValidMuxPlaybackId };

/** Fallback VIP universal (seed migration 046 / dual CTA). creator_id: juaperrone */
export const DEFAULT_BRAND_MUX_FALLBACK_PLAYBACK_ID =
  "JoJ2wxepBQf65hfnbQfs1cC5Kdaz8BtrK9oBZ6Ie85Y";

/** @deprecated Usar solo DEFAULT_BRAND_MUX_FALLBACK_PLAYBACK_ID / VITE_MUX_FALLBACK_PLAYBACK_ID. */
export const LEGACY_FEED_FALLBACK_PLAYBACK_IDS: readonly string[] = [
  DEFAULT_BRAND_MUX_FALLBACK_PLAYBACK_ID,
] as const;

function firstValidPlaybackId(
  candidates: Array<string | undefined | null>,
): string | null {
  for (const raw of candidates) {
    const t = typeof raw === "string" ? raw.trim() : "";
    if (isValidMuxPlaybackId(t)) return t;
  }
  return null;
}

/**
 * Brand-wide fallback playback ID (VIP hero + error recovery).
 * Orden: VITE_MUX_FALLBACK_PLAYBACK_ID → VITE_PREMIUM_MUX_* → default hardcoded.
 */
export function resolveBrandMuxFallbackPlaybackId(): string {
  return (
    firstValidPlaybackId([
      import.meta.env.VITE_MUX_FALLBACK_PLAYBACK_ID,
      import.meta.env.VITE_PREMIUM_MUX_VIP,
      import.meta.env.VITE_PREMIUM_MUX_ES_VIP,
      import.meta.env.VITE_PREMIUM_MUX_EN_VIP,
    ]) ?? DEFAULT_BRAND_MUX_FALLBACK_PLAYBACK_ID
  );
}

/** Solo ENV — null si ninguna var seteada (para detectar config incompleta en admin). */
export function resolvePremiumHeroPlaybackIdFromEnvOnly(): string | null {
  return firstValidPlaybackId([
    import.meta.env.VITE_PREMIUM_MUX_VIP,
    import.meta.env.VITE_PREMIUM_MUX_ES_VIP,
    import.meta.env.VITE_PREMIUM_MUX_EN_VIP,
    import.meta.env.VITE_MUX_FALLBACK_PLAYBACK_ID,
  ]);
}

/**
 * Hero VIP cuando no hay variant admin: ENV explícito o fallback brand (nunca null).
 * Usado en DualCTAVideoLanding / VideoTemplate.
 */
export function resolvePremiumHeroPlaybackId(): string {
  return resolvePremiumHeroPlaybackIdFromEnvOnly() ?? resolveBrandMuxFallbackPlaybackId();
}

/**
 * Recuperación ante error de red/token/decode: intentar el fallback brand
 * si es distinto del ID que falló. No usar para 404/invalid (config rota).
 */
export function resolveMuxErrorFallbackPlaybackId(
  failedPlaybackId: string | null | undefined,
): string | null {
  const fallback = resolveBrandMuxFallbackPlaybackId();
  const failed = (failedPlaybackId ?? "").trim();
  if (!isValidMuxPlaybackId(fallback)) return null;
  if (!failed || failed === fallback) return null;
  return fallback;
}

/**
 * IDs para galería BaseTemplate cuando /api/mux-videos falla.
 * Prioridad: VITE_MUX_FALLBACK_PLAYBACK_IDS (lista) → VITE_MUX_FALLBACK_PLAYBACK_ID.
 */
export function resolveGalleryFallbackPlaybackIds(): string[] {
  const envList = import.meta.env.VITE_MUX_FALLBACK_PLAYBACK_IDS;
  if (envList && typeof envList === "string") {
    const ids = envList
      .split(",")
      .map((id) => id.trim())
      .filter(isValidMuxPlaybackId);
    if (ids.length > 0) return [...new Set(ids)];
  }

  return [resolveBrandMuxFallbackPlaybackId()];
}

export { BRAND_MUX_CREATOR_ID };
